import React, { useEffect } from 'react';

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-gray-50">
      <section className="bg-gradient-to-br from-primary-700 to-primary-800 text-white py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">PRIVACY NOTICE</h1>
            <p className="opacity-90">Last updated August 25, 2025</p>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6 md:p-10 prose prose-gray max-w-none">
              <p>
                This privacy notice for Golf Marshal, LLC ("Company," "we," "us," or "our"), describes how and why we might collect, store, use, and/or share ("process") your information when you use our services ("Services"), such as when you:
              </p>
              <ul>
                <li>
                  Visit our website at <a href="https://www.getgolfmarshal.com/" target="_blank" rel="noopener noreferrer">https://www.getgolfmarshal.com/</a>, or any website of ours that links to this privacy notice
                </li>
                <li>
                  Engage with us in other related ways, including any sales, marketing, or events
                </li>
              </ul>
              <p>
                <strong>Questions or concerns?</strong> Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:support@getgolfmarshal.com">support@getgolfmarshal.com</a>.
              </p>

              <h2>SUMMARY OF KEY POINTS</h2>
              <p>
                This summary provides key points from our privacy notice, but you can find out more details about any of these topics by using our table of contents below to find the section you are looking for.
              </p>
              <ul>
                <li>
                  <strong>What personal information do we process?</strong> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with Golf Marshal, LLC and the Services, the choices you make, and the products and features you use.
                </li>
                <li>
                  <strong>Do we process any sensitive personal information?</strong> We do not process sensitive personal information.
                </li>
                <li>
                  <strong>Do we receive any information from third parties?</strong> We do not receive any information from third parties.
                </li>
                <li>
                  <strong>How do we process your information?</strong> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so.
                </li>
                <li>
                  <strong>In what situations and with which parties do we share personal information?</strong> We may share information in specific situations and with specific third parties.
                </li>
                <li>
                  <strong>How do we keep your information safe?</strong> We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure.
                </li>
                <li>
                  <strong>What are your rights?</strong> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.
                </li>
                <li>
                  <strong>How do you exercise your rights?</strong> The easiest way to exercise your rights is by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.
                </li>
              </ul>

              <h2>TABLE OF CONTENTS</h2>
              <ol>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-1">WHAT INFORMATION DO WE COLLECT?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-2">HOW DO WE PROCESS YOUR INFORMATION?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-3">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-4">HOW LONG DO WE KEEP YOUR INFORMATION?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-5">HOW DO WE KEEP YOUR INFORMATION SAFE?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-6">DO WE COLLECT INFORMATION FROM MINORS?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-7">WHAT ARE YOUR PRIVACY RIGHTS?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-8">CONTROLS FOR DO-NOT-TRACK FEATURES</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-9">DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-10">DO WE MAKE UPDATES TO THIS NOTICE?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-11">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</a></li>
                <li><a className="text-blue-600 hover:text-blue-700 underline" href="#section-12">HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</a></li>
              </ol>

              <h3 id="section-1">1. WHAT INFORMATION DO WE COLLECT?</h3>
              <h4>Personal information you disclose to us</h4>
              <p><em>In Short: We collect personal information that you provide to us.</em></p>
              <p>
                We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
              </p>
              <p>
                <strong>Personal Information Provided by You.</strong> The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following: email addresses.
              </p>
              <p>
                <strong>Sensitive Information.</strong> We do not process sensitive information.
              </p>
              <p>
                <strong>Payment Data.</strong> We may collect data necessary to process your payment if you make purchases, such as your payment instrument number (such as a credit card number), and the security code associated with your payment instrument. All payment data is stored by Stripe, Inc.. You may find their privacy notice here: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>.
              </p>
              <p>
                All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
              </p>

              <h4>Information automatically collected</h4>
              <p><em>In Short: Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.</em></p>
              <p>
                We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information.
              </p>
              <ul>
                <li>
                  <strong>Log and Usage Data.</strong> Service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services.
                </li>
                <li>
                  <strong>Device Data.</strong> Information about your computer, phone, tablet, or other device you use to access the Services.
                </li>
                <li>
                  <strong>Location Data.</strong> Information about your device's location, which can be either precise or imprecise.
                </li>
              </ul>

              <h3 id="section-2">2. HOW DO WE PROCESS YOUR INFORMATION?</h3>
              <p>
                <em>In Short:</em> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.
              </p>
              <ul>
                <li>To facilitate account creation and authentication and otherwise manage user accounts.</li>
                <li>To deliver and facilitate delivery of services to the user.</li>
                <li>To post testimonials.</li>
                <li>To evaluate and improve our Services, products, marketing, and your experience.</li>
                <li>To identify usage trends.</li>
              </ul>

              <h3 id="section-3">3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h3>
              <p>
                <em>In Short:</em> We may share information in specific situations described in this section and/or with the following third parties, such as during business transfers (mergers, acquisitions, or financing events).
              </p>

              <h3 id="section-4">4. HOW LONG DO WE KEEP YOUR INFORMATION?</h3>
              <p>
                <em>In Short:</em> We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.
              </p>

              <h3 id="section-5">5. HOW DO WE KEEP YOUR INFORMATION SAFE?</h3>
              <p>
                <em>In Short:</em> We aim to protect your personal information through a system of organizational and technical security measures, but no method is 100% secure.
              </p>

              <h3 id="section-6">6. DO WE COLLECT INFORMATION FROM MINORS?</h3>
              <p>
                <em>In Short:</em> We do not knowingly collect data from or market to children under 18 years of age.
              </p>

              <h3 id="section-7">7. WHAT ARE YOUR PRIVACY RIGHTS?</h3>
              <p>
                <em>In Short:</em> You may review, change, or terminate your account at any time. If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your local data protection supervisory authority. See: <a href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank" rel="noopener noreferrer">EU Supervisory Authorities</a>. If you are located in Switzerland: <a href="https://www.edoeb.admin.ch/edoeb/en/home.html" target="_blank" rel="noopener noreferrer">Swiss FDPIC</a>.
              </p>
              <p>
                Withdrawing your consent: You can withdraw consent at any time by contacting us at <a href="mailto:support@getgolfmarshal.com">support@getgolfmarshal.com</a>.
              </p>

              <h3 id="section-8">8. CONTROLS FOR DO-NOT-TRACK FEATURES</h3>
              <p>
                Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting. We do not currently respond to DNT signals.
              </p>

              <h3 id="section-9">9. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h3>
              <p>
                <em>In Short:</em> Yes. California residents have specific rights regarding access to their personal information (including "Shine the Light" and CCPA rights). To exercise these rights, contact us at <a href="mailto:support@getgolfmarshal.com">support@getgolfmarshal.com</a>.
              </p>

              <h3 id="section-10">10. DO WE MAKE UPDATES TO THIS NOTICE?</h3>
              <p>
                <em>In Short:</em> Yes, we will update this notice as necessary to stay compliant with relevant laws. The updated version will be effective as soon as it is accessible.
              </p>

              <h3 id="section-11">11. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h3>
              <p>
                If you have questions or comments about this notice, you may email us at <a href="mailto:support@getgolfmarshal.com">support@getgolfmarshal.com</a>.
              </p>
              <p>Golf Marshal, LLC</p>

              <h3 id="section-12">12. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h3>
              <p>
                Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To submit a request, please contact us at <a href="mailto:support@getgolfmarshal.com">support@getgolfmarshal.com</a>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;


